import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, AlertOctagon, Timer, PackageX, CheckCircle2, Building2, Search as SearchIcon, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

// ---------- Shared atoms ----------
export const KPI = ({ label, value, sub, Icon, tone = "steel", testId }) => {
  const toneMap = {
    steel: "bg-[#a8dadc]/30 text-[#457b9d]",
    red: "bg-[#e63946]/10 text-[#e63946]",
    green: "bg-[#12B76A]/10 text-[#12B76A]",
    orange: "bg-[#F79009]/10 text-[#F79009]",
    blue: "bg-[#457b9d]/15 text-[#457b9d]",
    honeydew: "bg-[#F1FAEE] text-[#1d3557]",
  };
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4 h-full" data-testid={testId}>
      {Icon && (
        <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${toneMap[tone] || toneMap.steel}`}>
          <Icon size={17} strokeWidth={2} />
        </div>
      )}
      <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className="text-[26px] md:text-[30px] font-bold text-[#1d3557] leading-tight mt-1 font-mono">{value}</div>
      {sub && <div className="text-[11px] text-[#667085] mt-0.5">{sub}</div>}
    </div>
  );
};

export const SectionCard = ({ title, subtitle, right, children, testId }) => (
  <div className="bg-white rounded-xl border border-[#E4E7EC]" data-testid={testId}>
    {(title || right) && (
      <div className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-[#1d3557]">{title}</h3>
          {subtitle && <div className="text-[11px] text-[#667085] mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
    )}
    <div>{children}</div>
  </div>
);

export const StatusChip = ({ status }) => {
  const cfg = {
    draft: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Draft" },
    vehicle_received: { cls: "bg-[#a8dadc]/25 text-[#1d3557] border-[#a8dadc]/60", label: "Vehicle Received" },
    inspection_pending: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Inspection" },
    estimate_pending: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Estimate" },
    approval_pending: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Approval" },
    work_in_progress: { cls: "bg-[#457b9d]/15 text-[#1d3557] border-[#457b9d]/30", label: "In Progress" },
    waiting_for_parts: { cls: "bg-[#e63946]/15 text-[#e63946] border-[#e63946]/30", label: "Waiting Parts" },
    work_completed: { cls: "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25", label: "Work Complete" },
    quality_check: { cls: "bg-[#a8dadc]/25 text-[#1d3557] border-[#a8dadc]/60", label: "QC" },
    ready_for_delivery: { cls: "bg-[#F1FAEE] text-[#027A48] border-[#12B76A]/25", label: "Ready" },
    delivered: { cls: "bg-[#F1FAEE] text-[#027A48] border-[#12B76A]/25", label: "Delivered" },
    closed: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Closed" },
    cancelled: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Cancelled" },
  }[status] || { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: status || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${cfg.cls}`}>{cfg.label}</span>;
};

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(v); }
};

// ---------- Page ----------
export default function AdminJobCards() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [highValueOnly, setHighValueOnly] = useState(false);
  const [sortByValue, setSortByValue] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const params = { q: q || undefined, status: status || undefined, limit: 150 };
    if (highValueOnly) params.min_value = 100000;
    if (sortByValue) params.sort = "value_desc";
    const [s, l] = await Promise.all([
      api.get("/admin/ops/job-cards/summary").then((r) => r.data).catch(() => null),
      api.get("/admin/ops/job-cards", { params }).then((r) => r.data).catch(() => ({ rows: [] })),
    ]);
    setSummary(s);
    setRows(l.rows || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, [status, highValueOnly, sortByValue]);

  const kpis = summary?.kpis || {};
  const top = summary?.top_garages || [];

  return (
    <div className="space-y-6" data-testid="admin-job-cards-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Platform Ops</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Job Cards</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">Every active job card across every garage on the platform.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI testId="jc-kpi-total" label="Total" value={kpis.total ?? "—"} Icon={ClipboardList} sub={`+${kpis.created_this_week ?? 0} this week`} />
        <KPI testId="jc-kpi-active" label="Active" value={kpis.active ?? "—"} Icon={Timer} tone="blue" />
        <KPI testId="jc-kpi-delayed" label="Delayed" value={kpis.delayed ?? "—"} Icon={AlertOctagon} tone="red" />
        <KPI testId="jc-kpi-waiting" label="Waiting Parts" value={kpis.waiting_for_parts ?? "—"} Icon={PackageX} tone="orange" />
        <KPI testId="jc-kpi-ready" label="Ready" value={kpis.ready_for_delivery ?? "—"} Icon={CheckCircle2} tone="honeydew" />
        <KPI testId="jc-kpi-today" label="Today" value={kpis.created_today ?? "—"} Icon={ClipboardList} tone="green" sub="Created today" />
      </div>

      {/* Filters + list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <SectionCard
            title="Job Cards"
            subtitle={`${rows.length} row(s)`}
            right={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#667085]" />
                  <input
                    data-testid="jc-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
                    placeholder="JC#, customer, vehicle…"
                    className="pl-7 pr-3 h-8 rounded-md border border-[#E4E7EC] bg-white text-[12px] w-56 focus:outline-none focus:border-[#457b9d]"
                  />
                </div>
                <select
                  data-testid="jc-status-filter"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="h-8 px-2 rounded-md border border-[#E4E7EC] bg-white text-[12px] focus:outline-none focus:border-[#457b9d]"
                >
                  <option value="">All statuses</option>
                  <option value="vehicle_received">Vehicle Received</option>
                  <option value="inspection_pending">Inspection Pending</option>
                  <option value="estimate_pending">Estimate Pending</option>
                  <option value="approval_pending">Approval Pending</option>
                  <option value="work_in_progress">Work In Progress</option>
                  <option value="waiting_for_parts">Waiting Parts</option>
                  <option value="work_completed">Work Completed</option>
                  <option value="quality_check">Quality Check</option>
                  <option value="ready_for_delivery">Ready for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={() => setHighValueOnly((v) => !v)}
                  data-testid="jc-high-value-chip"
                  className={`h-8 px-2.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${highValueOnly ? "bg-[#e63946] text-white border-[#e63946]" : "bg-white text-[#e63946] border-[#e63946]/30 hover:bg-[#e63946]/5"}`}
                  title="Filter to Job Cards ≥ ₹1,00,000"
                >
                  ₹1L+
                </button>
                <button
                  onClick={() => setSortByValue((v) => !v)}
                  data-testid="jc-sort-value"
                  className={`h-8 px-2.5 rounded-md text-[11px] font-semibold border ${sortByValue ? "bg-[#1d3557] text-white border-[#1d3557]" : "bg-white text-[#1d3557] border-[#E4E7EC] hover:border-[#1d3557]"}`}
                  title="Sort by value (highest first)"
                >
                  Sort ₹↓
                </button>
                <button onClick={reload} data-testid="jc-refresh" className="h-8 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152941]">Refresh</button>
              </div>
            }
            testId="jc-table-card"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px]">
                <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="px-4 py-2 font-semibold">JC #</th>
                    <th className="px-4 py-2 font-semibold">Garage</th>
                    <th className="px-4 py-2 font-semibold">Customer</th>
                    <th className="px-4 py-2 font-semibold">Vehicle</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2 font-semibold text-right">Value</th>
                    <th className="px-4 py-2 font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1FAEE]">
                  {loading && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-[#667085]">No job cards match.</td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr key={r.id} data-testid={`jc-row-${r.id}`} onClick={() => window.location.assign(`/admin/job-cards/${r.id}`)} className="hover:bg-[#F7F9FC] cursor-pointer">
                      <td className="px-4 py-2 font-mono font-semibold text-[#1d3557]">
                        {r.number || <span className="text-[#667085] italic font-sans font-normal">#{(r.id || "").slice(0, 8)}</span>}
                      </td>
                      <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                        <Link to={`/admin/garages/${r.garage_id}`} className="text-[#457b9d] hover:underline">{r.garage_name}</Link>
                      </td>
                      <td className="px-4 py-2 text-[#1d3557]">{r.customer_name || "—"}</td>
                      <td className="px-4 py-2 font-mono">{r.vehicle_reg || "—"}</td>
                      <td className="px-4 py-2"><StatusChip status={r.status} /></td>
                      <td className="px-4 py-2 text-right">
                        <div className="font-mono font-semibold text-[#1d3557]">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(r.value || 0))}</div>
                        {r.high_value && <span className="inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider bg-[#e63946]/8 text-[#e63946] border-[#e63946]/30 mt-0.5">High Value</span>}
                      </td>
                      <td className="px-4 py-2 text-[#667085]">{fmtDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Top Garages by Open JCs" testId="jc-top-garages">
            <div className="divide-y divide-[#F1FAEE]">
              {top.length === 0 && <div className="p-6 text-center text-[13px] text-[#667085]">No data</div>}
              {top.map((g, i) => (
                <Link key={g.garage_id} to={`/admin/garages/${g.garage_id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC]">
                  <div className="w-7 h-7 rounded-md grid place-items-center bg-[#F1FAEE] text-[#457b9d]">
                    <Building2 size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#1d3557] truncate">#{i + 1} {g.garage_name}</div>
                    <div className="text-[11px] text-[#667085] truncate">{g.city || "—"}</div>
                  </div>
                  <div className="text-[13px] font-mono font-bold text-[#1d3557]">{g.open_count}</div>
                  <ChevronRight size={13} className="text-[#a8dadc]" />
                </Link>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Status Breakdown" testId="jc-status-breakdown">
            <div className="p-5 space-y-2">
              {Object.entries(summary?.by_status || {}).sort(([, a], [, b]) => b - a).map(([s, n]) => (
                <div key={s} className="flex items-center gap-3">
                  <StatusChip status={s} />
                  <div className="flex-1 h-1.5 bg-[#F1FAEE] rounded">
                    <div className="h-full bg-[#457b9d] rounded" style={{ width: `${Math.min(100, (n / Math.max(1, kpis.total || 1)) * 100)}%` }} />
                  </div>
                  <div className="text-[12px] font-mono font-bold text-[#1d3557] w-10 text-right">{n}</div>
                </div>
              ))}
              {Object.keys(summary?.by_status || {}).length === 0 && (
                <div className="text-[13px] text-[#667085] text-center">No data</div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
